import { useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useId, useState } from 'react';

import {
  type CreateEngagementInput,
  EngagementCreationPath,
  type EngagementPricingPreviewQuery,
} from '@/__generated__/graphql';
import Avatar from '@/auth/components/Avatar';
import Button from '@/componentsv2/Button';
import { Drawer } from '@/componentsv2/Drawer';
import InfoTip from '@/componentsv2/InfoTip';
import Input from '@/componentsv2/Input';
import { ERROR_CODES, firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { type ActiveAccount, useActiveAccount } from '../featureFlag';
import { createDenialMessage, createDenialMessageForCode } from './create-denial';
import { HIRE_DRAWER_TITLE } from './hireCopy';
import {
  CREATE_ENGAGEMENT,
  ENGAGEMENT_CREATION_CONTEXT,
  ENGAGEMENT_PRICING_PREVIEW,
  HIRE_EXPERT_ACCOUNT_MIN_HOURS,
  HIRE_EXPERT_PROJECT_OPTIONS,
} from './operations';
import { TERMS } from './termCopy';

export interface HireExpertSource {
  creationPath: EngagementCreationPath;
  /**
   * The project the engagement is created under, when the entry point fixes it.
   *
   * Absent on `from_profile`, which is the one designed entry where the customer
   * arrives with an expert and no project, so the drawer asks. The prototype's
   * `HireExpertDrawer/ProjectSection.tsx` splits it the same way: fixed and
   * read-only on the other paths, a required select here.
   */
  projectId?: string;
  /** Exactly one of these, matching the creation path; the server validates. */
  originatingConsultationId?: string;
  sourceCandidateId?: string;
  profileId?: string;
  expertId?: string;
  /**
   * The expert's display name, for the read-only Expert section (ENG-5734).
   *
   * A prop rather than a read. Every source-bearing entry point already holds
   * the name it navigated from — `consultation.expert.name` on the
   * consultation header, `profile.name` on the profile sidebar — so resolving
   * it again here would be a second round trip for a string the caller is
   * already rendering. Optional because it is presentation only: without it
   * the section is omitted rather than shown nameless.
   */
  expertName?: string;
}

/** `CreateEngagementInput.requestScopeDescription` is capped server-side. */
const SCOPE_MAX = 2000;

/**
 * How many projects the picker offers, and the server's own maximum
 * (`paging.ValidatePage` refuses anything over 1000).
 *
 * It is pinned at the ceiling because a native `<select>` cannot paginate:
 * there is nowhere to put a "load more" inside an options list, so a single
 * request that cannot truncate is the only honest option. It was 100, which was
 * a real cliff — past it the customer simply could not reach their project, and
 * nothing said so. Now unreachable in practice: the read is narrowed to one
 * account AND to projects the viewer created or was invited to (creating a
 * project adds the creator alone), and the largest account on the platform has
 * high hundreds of projects in total across all its members.
 *
 * A searchable single-select would still be the better control for a long list.
 * That needs a `componentsv2` primitive that does not exist yet — see
 * `ProjectPicker` — and is tracked separately rather than grown inside this
 * drawer.
 */
const PROJECT_OPTIONS_PAGE_SIZE = 1000;

const PROJECT_SELECT_ID = 'hire-expert-project';

/**
 * Preview failures that are settled answers rather than transient ones. A retry
 * against either is an endless loop: the expert has no usable rate, or the
 * account is on a plan the api refuses engagement billing settings for (SMB is
 * postpaid, and `enableEngagementBillingSettings` rejects it).
 */
const SETTLED_PREVIEW_CODES: ReadonlySet<string> = new Set<string>([
  ERROR_CODES.ENGAGEMENT_PREVIEW_RATE_UNAVAILABLE,
  // Both billing-settings refusals, matching `handleSubmit`'s terminal set.
  // The preview reads the same settings the create does, so an account with
  // none answers `not_enabled` here too, and offering "try again" against an
  // answer this same file calls terminal is the loop this set exists to stop.
  ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_PLAN_NOT_ELIGIBLE,
  ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_NOT_ENABLED,
]);
const PROJECT_ERROR_ID = 'hire-expert-project-error';

interface HireExpertDrawerProps {
  open: boolean;
  onClose: () => void;
  source: HireExpertSource;
  onCreated: (engagementId: string) => void;
}

/**
 * Engagement creation, wired to `createEngagement`.
 *
 * **Participants are removed, not disabled.** `CreateEngagementInput` has no
 * participant fields (ENG-5407's server defaults apply,
 * `engagement_gql.go:922`), so the prototype's section would have collected
 * values with nowhere to go. It returns additively if the input grows.
 * Pacing, End date and Billing cadence are absent for a different reason:
 * Brian cut them from V1 by design (FB-180, 2026-09-04).
 *
 * **Scope is NON-EMPTY here and nowhere else.** The api does bound this field
 * — `engagement/service/create.go` trims it (`:127`) and refuses more than
 * `maxScopeLen` 2000 runes (`:195`, `ErrScopeTooLong`) — but it accepts an
 * empty one, so only the non-empty rule is this drawer's alone. That is
 * enough because this drawer is the only writer, and the rule has to hold
 * somewhere: an expert who receives a request with no description of the work
 * has nothing to price against (ENG-5734).
 *
 * The client's cap is the stricter of the two in both dimensions, counting
 * UTF-16 units where the server counts runes, so it can only ever over-reject
 * and never send a scope the api would refuse.
 *
 * **Pricing comes from the server, never from a client-side computation.** A
 * preview needs the account take rates — staff-gated — and the expert's
 * underlying rate, which the customer must never see (§6.3), so
 * `engagementPricingPreview` resolves it server-side and returns only
 * customer-facing numbers.
 */
export default function HireExpertDrawer({
  open,
  onClose,
  source,
  onCreated,
}: HireExpertDrawerProps): JSX.Element {
  const [hours, setHours] = useState('');
  const [scope, setScope] = useState('');
  const [touched, setTouched] = useState(false);
  // A `type="number"` input reports a blank `.value` for every intermediate
  // string a decimal passes through — "2.", "-", "1e" — per the HTML
  // value-sanitization algorithm, so the state goes empty mid-typing while the
  // characters stay on screen. Nagging about a missing number in that moment
  // reads as a validation failure the customer cannot see the cause of, so the
  // blank message waits until the caret leaves. Verified in the browser: typing
  // "2." into a 2.5 replaced the helper text for one keystroke.
  const [hoursFocused, setHoursFocused] = useState(false);
  // Whether the customer has been IN the hours field, as distinct from having
  // engaged with the form at all (`touched`).
  //
  // Scope now sits above Hours, so the ordinary top-to-bottom flow reaches
  // Scope first. On one shared flag, typing the scope revealed "Enter a number
  // of hours." under a field the customer had not arrived at yet — the same
  // nagging `hoursFocused` exists to prevent, arriving by a different route.
  // `touched` still drives the fields ABOVE this one, where surfacing a blank
  // the customer has already walked past is the point.
  //
  // Only the BLANK message needs it. A value that is present but wrong is
  // worth saying immediately, and reaching that branch means the field was
  // edited anyway. Nor does a submit attempt need to force it: `canSubmit`
  // requires non-blank hours, so Send cannot be pressed in this state.
  const [hoursEdited, setHoursEdited] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [engagementsUnavailable, setEngagementsUnavailable] = useState(false);
  const [pickedProjectId, setPickedProjectId] = useState('');

  // The entry point's project when it has one, otherwise the viewer's pick.
  // Everything downstream reads this rather than `source.projectId`, because the
  // account minimum and the price both depend on which project is chosen.
  const mustPickProject = source.projectId === undefined;
  const projectId = source.projectId ?? pickedProjectId;

  // The account the viewer is acting in, which scopes the picker. Read
  // unconditionally — hooks cannot be called behind the `mustPickProject`
  // branch — and consumed only on the path that asks for a project.
  //
  // The options read itself lives in `ProjectPickerSection`, which takes a
  // REQUIRED account. That is what keeps `group_id: ""` off the wire: an empty
  // group id reads api-side as "no group filter", which is every account the
  // viewer belongs to — the ENG-5729 leak this scoping exists to close. Holding
  // the query here would mean building that value and relying on a `skip` to
  // suppress it, so the invariant would be two expressions agreeing rather than
  // a type. Reviewers flagged that line twice; this removes the value instead.
  const activeAccount = useActiveAccount();

  const context = useQuery(ENGAGEMENT_CREATION_CONTEXT, {
    variables: { projectId },
    // Nothing to read until a project is settled: the query is the account's
    // minimum hours, which is a property of the project's billing account.
    skip: !open || projectId === '',
  });
  const [createEngagement, { loading: submitting }] = useMutation(CREATE_ENGAGEMENT);

  // The floor is a property of the billing ACCOUNT, but the read above is keyed
  // on a project, and `from_profile` opens with none chosen. The field copy
  // states the floor unconditionally (FB-181), so on that one path the account
  // has to answer first. Same `activeAccount` the picker scopes itself with, so
  // see its doc block for why a group id off `viewer.groups` is safe to hand to
  // a group-scoped read and what `undefined` covers.
  const accountContext = useQuery(HIRE_EXPERT_ACCOUNT_MIN_HOURS, {
    variables: { groupId: activeAccount?.id ?? '' },
    // Only the entry that has to ASK for a project needs this. The other three
    // arrive with one, so the project-keyed read answers at open and this would
    // be a second request for a number already on its way. Worse than waste:
    // `hoursError` reads the same value, so on a project belonging to a
    // different account than the viewer's context it could refuse valid input
    // for a frame, quoting a floor that does not apply to it.
    //
    // Deliberately not `projectId === ''`, which would drop the answer the
    // moment the customer picks a project and leave the floor missing from the
    // copy for as long as the project read is in flight. The window this fills
    // is the whole picker flow, not just its first frame.
    //
    // A blank id here is not the widening read it would be for ENG-5729's
    // `projects(group_id:)`. `group(id: "")` fails its own lookup and errors,
    // so the skip is guarding against a pointless error rather than a leak.
    skip: !open || activeAccount === undefined || !mustPickProject,
  });

  const project = context.data?.project;

  // ENG-5727 pre-submission refusal. The profile path has no project until the
  // picker above settles one, so this is the earliest point the authority
  // question can be asked there; the consultation path already answered it
  // before opening.
  //
  // `createDenialMessage` directly, NOT `resolveCreateAffordance`: that helper's
  // third outcome is "hide the affordance", which has no meaning in a drawer the
  // customer has already opened. Every reason gets stated here, entitlement
  // included — silently refusing to submit would be the dead end this ticket is
  // about.
  //
  // Fails OPEN while the query is in flight or if it errors, because `project`
  // is then undefined and no reason is known. That is deliberate: this is
  // messaging, not a gate. The mutation is the gate, and it refuses with the
  // same copy via `createDenialMessageForCode` below.
  // `?? null` collapses both "not loaded yet" and "the api says yes" into the
  // same no-refusal answer, which is the fail-open direction described above.
  const createDenial = project?.viewer_engagement_create_denial ?? null;
  const createRefusal =
    createDenial === null
      ? null
      : createDenialMessage(createDenial, {
          accountName: project?.group?.name,
          projectName: project?.name,
        });

  // The project wins once it lands: the engagement is billed to the project's
  // account, so that is the account whose minimum applies. The account read is
  // the same number in practice, since the picker only offers projects from it,
  // and it is here to fill the window before a project is settled rather than
  // to approximate a different answer. Both feed one value, because a helper
  // text and a refusal quoting different floors is worse than either alone.
  const minHours =
    project?.group?.billing_account?.engagementBillingSettings?.engagementMinHours ??
    accountContext.data?.group?.billing_account?.engagementBillingSettings?.engagementMinHours ??
    null;
  const hoursValue = Number.parseFloat(hours);
  // Blank is an error once the customer has engaged with the form, and blocks
  // submit either way. It used to be neither: `canSubmit` never required hours,
  // so on a path with a fixed project Send was live on a freshly-opened drawer
  // and one click created a real engagement with no NTE and no price ever shown.
  // The api is no backstop — `estimatedHours` is optional there and the account
  // minimum is enforced at Make Offer, not at create — so the ceiling has to
  // hold here.
  const hoursMissing = hours === '';
  const hoursError = hoursMissing
    ? hoursEdited && !hoursFocused
      ? 'Enter a number of hours.'
      : null
    : !Number.isFinite(hoursValue) || hoursValue <= 0
      ? 'Enter a number greater than zero.'
      : minHours !== null && hoursValue < minHours
        ? `This account has a ${minHours}-hour minimum.`
        : null;
  // Required per spec §9.2 and the prototype's own validator
  // (`components/HireExpertDrawer/form.ts`), which refuses a scope that is
  // blank after trimming. Whitespace has to count as blank or the rule is
  // decorative: a single space would satisfy a length check and still reach
  // the expert as an empty request. Same shape as the hours field above, so
  // the message waits for `touched` while the block does not.
  //
  // Both rules read the TRIMMED value, which is also what submits. Measuring
  // the raw string against the cap while sending the trimmed one refused a
  // scope that would have been accepted: 2000 characters plus a trailing
  // newline is 2001 by `scope.length` and 2000 by the time it reaches the api.
  const trimmedScope = scope.trim();
  const scopeMissing = trimmedScope === '';
  const scopeError = scopeMissing
    ? touched
      ? 'Describe the work you need.'
      : null
    : trimmedScope.length > SCOPE_MAX
      ? `Keep the scope to ${SCOPE_MAX} characters or fewer.`
      : null;
  const projectError = touched && projectId === '' ? 'Choose a project.' : null;
  // Deliberately NOT gated on `projectId !== ''`. Disabling Send for a missing
  // project makes `handleSubmit`'s `setTouched(true)` unreachable, so the
  // customer gets a dead primary action and never learns why. Let the click
  // through and let the guard below refuse it with a stated reason.
  const canSubmit =
    !hoursMissing &&
    !hoursError &&
    !scopeMissing &&
    !scopeError &&
    !submitting &&
    !engagementsUnavailable &&
    // Unlike the missing-project case below, this one IS gated: the reason is
    // already on screen, so a live Send would just round-trip to the same
    // sentence.
    createRefusal === null;

  // `customer_introduced` is the one creation path the preview cannot represent.
  // `EngagementPricingPreviewInput` carries only the three provenance refs, and
  // the api has no preview for a path that has no expert to walk to — "that path
  // carries no source ref, so it has no expert to price until one is attached"
  // (`preview.go`); it may even open expertless. A ref-less input would earn
  // `engagement.invalid_source_ref` for every priceable size and surface as a
  // retry that can never succeed, so the preview is not attempted at all.
  const previewable = source.creationPath !== EngagementCreationPath.CustomerIntroduced;

  // What is keeping the server from answering, if anything. The notice reads
  // this rather than inferring it from a null preview: a skipped query and an
  // empty hours field are NOT the same thing — the query is also skipped with no
  // project picked, and for hours that are present but invalid or below the
  // account minimum — and a notice that inferred "no answer" to mean "no hours"
  // told a customer who had just typed 4 hours to enter some (webapp#2877
  // review).
  const previewBlockedOn: PreviewBlock = !previewable
    ? null
    : projectId === ''
      ? 'project'
      : hours === ''
        ? 'hours'
        : hoursError !== null
          ? 'invalid-hours'
          : null;

  // Price only a size the server would accept, and send exactly one source ref
  // — the same key the create mutation will carry. Derived from the block above
  // rather than restating its conditions, so the two cannot drift.
  const priceable = previewable && previewBlockedOn === null;
  const preview = useQuery(ENGAGEMENT_PRICING_PREVIEW, {
    variables: {
      input: {
        projectId,
        nteHours: priceable ? hoursValue.toFixed(2) : '',
        ...(source.profileId ? { profileId: source.profileId } : {}),
        ...(source.sourceCandidateId ? { sourceCandidateId: source.sourceCandidateId } : {}),
        ...(source.originatingConsultationId
          ? { originatingConsultationId: source.originatingConsultationId }
          : {}),
      },
    },
    skip: !open || !priceable,
    // So a refetch flips `loading` the way the first fetch does. Without it
    // Apollo leaves `loading` false through a retry while `error` stays set, so
    // "Check again" would sit there still offering itself, with nothing on
    // screen changing, for as long as the round trip took — and a second click
    // would start an overlapping request (webapp#2877 review).
    notifyOnNetworkStatusChange: true,
  });

  async function handleSubmit() {
    setTouched(true);
    if (!canSubmit || projectId === '') return;
    setSubmitError(null);
    const input: CreateEngagementInput = {
      creationPath: source.creationPath,
      projectId,
      originatingConsultationId: source.originatingConsultationId,
      sourceCandidateId: source.sourceCandidateId,
      profileId: source.profileId,
      expertId: source.expertId,
      estimatedHours: Number.isFinite(hoursValue) ? hoursValue : undefined,
      requestScopeDescription: trimmedScope,
    };
    try {
      const { data } = await createEngagement({ variables: { input } });
      if (data?.createEngagement.id) onCreated(data.createEngagement.id);
    } catch (err) {
      const code = firstErrorCode(err);
      // Both of these are terminal, not retryable: the account either has no
      // engagement billing settings or is on a plan the api refuses them for
      // (SMB is postpaid, and `enableEngagementBillingSettings` rejects it). No
      // amount of re-submitting turns either into a yes.
      if (
        code === ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_NOT_ENABLED ||
        code === ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_PLAN_NOT_ELIGIBLE
      ) {
        setEngagementsUnavailable(true);
      }
      // ENG-5727: a create-authority refusal names what the viewer is missing,
      // so it takes precedence over the generic per-code copy. This is the
      // BACKSTOP, not the primary path — the entry points read
      // `Project.viewer_engagement_create_denial` and explain before the user
      // gets here. It fires when a membership changed in between, and on the
      // profile path, where no project is known until this drawer.
      // Both names come from the context query, which is keyed on the project
      // actually being submitted and runs on BOTH entry paths. Reading them off
      // the options dropdown instead left every refusal saying "this project"
      // on the consultation path, where that query is skipped.
      const denial = createDenialMessageForCode(code, {
        accountName: project?.group?.name,
        projectName: project?.name,
      });
      // `project !== undefined` is the load-bearing half: it means this drawer
      // actually HAD the api's read-field answer and it said yes. Without it,
      // the expected case — context still in flight or errored, drawer fails
      // open, customer submits, api refuses — would log a warning on every
      // slow network, which is noise rather than signal.
      //
      // Deliberately conservative on the consultation path, where the page
      // query supplies the field and this drawer's own copy may not have
      // arrived: a real disagreement there can go unreported. Under-reporting a
      // warning beats crying wolf.
      if (denial !== null && project !== undefined) {
        // A create-authority refusal reaching the MUTATION means the read field
        // and the guard disagreed: the affordance was offered to someone the
        // api then refused. That is the exact failure ENG-5727 removes, and it
        // is otherwise invisible — the customer sees a sentence and moves on.
        //
        // `warning`, not `error`, matching MakeOfferDrawer: it is a coded,
        // already-handled condition with copy written for it.
        //
        // Both entry points now consult the read field before submitting — the
        // consultation page in its own query, the profile path via
        // `createRefusal` above — so reaching this at all means the field and
        // the guard disagreed. The benign explanation is a membership that
        // changed mid-session; a sustained rate is not benign.
        Sentry.captureException(err, {
          level: 'warning',
          tags: { engagement_create_denied_at_mutation: 'true' },
          extra: { code, creationPath: source.creationPath },
        });
      }
      setSubmitError(
        denial ?? getDefaultErrorMessage(code ?? '') ?? 'This engagement could not be created.'
      );
    }
  }

  return (
    <Drawer open={open} onClose={onClose} size="sm">
      {/* The one name for this action, shared with the entry-point CTAs
          that open the drawer. "Hire this expert" restated the button the
          customer just pressed; this names what the drawer creates. See
          `hireCopy.ts` for the prototype deviation it carries. */}
      <Drawer.Header onClose={onClose} breadcrumbs={[{ title: HIRE_DRAWER_TITLE }]} />
      <Drawer.Main>
        <div className="flex flex-col gap-24">
          {/* Read-only, and no email. The expert already has a platform
              profile, so surfacing an address here would invite off-platform
              contact (spec §11.1), which is why the prototype's
              `ExpertSection` withholds it on every source-bearing path too.
              Omitted rather than rendered nameless when the caller passes no
              name: an "Expert" card over a blank row states less than no card
              at all. */}
          {source.expertName ? (
            <Section title="Expert">
              <div className="flex items-center gap-12 rounded-md border border-surface-border bg-surface-elevated px-16 py-12">
                <Avatar fullName={source.expertName} sx={{ width: 40, height: 40 }} />
                <div className="flex flex-col">
                  <span className="text-on-surface subtitle-2">{source.expertName}</span>
                  <span className="text-on-surface-muted util-caption">
                    {expertSourceLine(source.creationPath)}
                  </span>
                </div>
              </div>
            </Section>
          ) : null}

          <Section title="Project">
            {mustPickProject ? (
              activeAccount ? (
                <ProjectPickerSection
                  open={open}
                  account={activeAccount}
                  value={pickedProjectId}
                  error={projectError}
                  onChange={(next) => {
                    // Both of these describe the PREVIOUS project's billing
                    // account, and the project is switchable within one drawer
                    // session now. Without this, one refusal from an ineligible
                    // account disables Send for the rest of the session,
                    // including after switching to an account that would accept
                    // it.
                    setPickedProjectId(next);
                    setEngagementsUnavailable(false);
                    setSubmitError(null);
                  }}
                />
              ) : (
                // No account context, so there is nothing to scope a read to and
                // no read is made. Unreachable through `HireExpertButton`, which
                // gates on the same context, and pinned by a test that renders
                // with no mocks so an issued query would error rather than idle.
                <ProjectRequiredNotice accountName={null} />
              )
            ) : context.loading ? (
              <p className="text-on-surface-muted body-2">Loading project…</p>
            ) : (
              <p className="text-on-surface body-2">
                This engagement will be created under{' '}
                <span className="font-medium">{project?.name ?? 'this project'}</span>.
              </p>
            )}
          </Section>

          {/* Above Hours, as in the prototype. The customer describes the work
              before sizing it, and the price the Hours field drives is only
              meaningful against a stated need. */}
          <Section title="Scope">
            <Input
              label="What do you need from this expert?"
              required
              multiline
              rows={4}
              value={scope}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTouched(true);
                setScope(e.target.value);
              }}
              error={touched && scopeError !== null}
              // ENG-5734 / FB-180. The shipped pair argued against its own field:
              // an `(optional)` label over a sentence saying the expert writes
              // the scope anyway, so both signals told the customer to skip the
              // one place in the flow where they say what they actually want.
              // The spec strings are restored verbatim. They instruct rather than
              // disclaim: what to write, and the three things worth including.
              //
              // `required` rather than a hand-written suffix. The primitive
              // appends "(required)" itself, which is how the sibling hours field
              // renders its own, so spelling it into the label would double it.
              helperText={
                touched && scopeError
                  ? scopeError
                  : 'Describe the work, deliverables, and any context the expert should know.'
              }
              placeholder="Multi-pass technical-volume review, written feedback, color-team participation."
            />
          </Section>

          <Section title="Hours">
            <Input
              label="Estimated hours"
              required
              type="number"
              // `step: 'any'` rather than the prototype's `1`. The api supports a
              // fractional minimum (its settings test round-trips 2.5) and this
              // field prices with `toFixed(2)`, so whole-hour stepping would put
              // the spinner, and the iOS numeric keypad's decimal key, at odds
              // with what the field accepts.
              inputProps={{ min: minHours ?? undefined, step: 'any' }}
              placeholder={minHours !== null ? String(minHours) : undefined}
              value={hours}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTouched(true);
                setHoursEdited(true);
                setHours(e.target.value);
              }}
              onFocus={() => setHoursFocused(true)}
              onBlur={() => setHoursFocused(false)}
              error={touched && hoursError !== null}
              // ENG-5735 / FB-181. The number is a not-to-exceed CEILING, and the
              // shipped copy called it "a provisional estimate" the expert sets.
              // Those are close to opposites: an estimate is a guess work may
              // exceed, a cap is a limit work may not. It also put the expert in
              // the subject position of the sentence that fixes the customer's
              // spending limit, which reads as the counterparty holding the
              // customer's cost control. The spec string is restored verbatim;
              // its three jobs are naming the concept, stating what happens at
              // the cap, and giving the floor.
              //
              // "Estimate" now survives only in the field's NAME, which is the
              // prototype's own label and stays. Every other string on this field
              // dropped it, the two refusals and the pricing prompt included:
              // they share this slot or sit directly under it, so a refusal
              // calling the number an estimate would undo the sentence above it.
              //
              // Two variants, not one. `engagement_min_hours` defaults to 1.00 in
              // the api, which is most accounts, so the singular is the common
              // case rather than a nicety; a fractional minimum stays plural, and
              // 2.5 is reachable. The floor is dropped entirely when no minimum
              // is known, which is an account with no settings row, a
              // `billing_account` degraded to null by permissions, or no account
              // context at all. Stating no floor beats stating a wrong one.
              //
              // ENG-5757: the floor carries the account setting's definition
              // from `termCopy.ts`, the same sentence the Settings tab shows.
              helperText={
                touched && hoursError ? (
                  hoursError
                ) : minHours !== null ? (
                  // One text node, then the tip: the sentence stays whole for
                  // anything that reads it as a unit, screen readers included.
                  <>
                    {`Minimum ${minHours} ${minHours === 1 ? 'hour' : 'hours'}. The not-to-exceed ceiling — extensions require an amendment later.`}
                    <InfoTip
                      className="ml-4 align-middle"
                      term={TERMS.minimumHours.label}
                      definition={TERMS.minimumHours.definition}
                    />
                  </>
                ) : (
                  'The not-to-exceed ceiling — extensions require an amendment later.'
                )
              }
            />
          </Section>

          {/* The section KEEPS its heading and loses only its body. Brian's own
              prototype does exactly this for the path that cannot be priced:
              `components/HireExpertDrawer/PricingPreviewSection.tsx` ships a
              placeholder variant that holds the "Estimated cost" heading and
              swaps the figures for a note. Dropping the heading instead would
              strand the note between two titled sections, and it would take
              ENG-5734's section count from five to four. */}
          <Section title="Estimated cost">
            <OfferPricingNotice
              previewable={previewable}
              blockedOn={previewBlockedOn}
              preview={preview.data?.engagementPricingPreview ?? null}
              loading={preview.loading}
              failed={preview.error !== undefined}
              onRetry={() => void preview.refetch()}
              // A retry is only honest for a transient failure. An unavailable
              // expert rate and an ineligible plan are both settled answers, and
              // offering "try again" against either is an endless loop.
              retryable={
                preview.error !== undefined &&
                !SETTLED_PREVIEW_CODES.has(firstErrorCode(preview.error) ?? '')
              }
            />
          </Section>

          {/* The consultation entry replaced a dialog whose whole job was saying
              "you will not be charged until we work out the details", so the
              reassurance has to survive the replacement. Sending a request is
              not a commitment: the expert prices it, and the customer accepts
              before anything bills. */}
          <p className="text-on-surface-muted body-2">
            Sending this doesn&rsquo;t commit you to anything. The expert can accept, decline, or
            counter before any billing starts.
          </p>

          {/* ENG-5727. Above the submit error rather than beside it: this one is
              known before the customer acts, and it is the reason Send is
              disabled. Shaped after `ConnectGateBanner`, the feature's existing
              blocking-state precedent — role="alert", one instructive sentence,
              action disabled by the composition root — because the prototype
              Storybook has no story for a creation refusal to diff against. */}
          {createRefusal ? (
            <div
              role="alert"
              className="rounded-md border border-yellow-700/40 bg-yellow-50 px-16 py-12 dark:bg-yellow-900/20"
            >
              <span className="text-yellow-900 body-2 dark:text-yellow-200">{createRefusal}</span>
            </div>
          ) : null}

          {submitError ? <p className="text-red-600 body-2">{submitError}</p> : null}
        </div>
      </Drawer.Main>
      {/* Actions are left-aligned, not the usual bottom-right: this drawer is
          full-height and right-anchored, so a `justify-end` footer parks the
          submit button under the app's fixed support launcher (HubSpot
          Conversations via GTM, and Intercom where `VITE_INTERCOM_APP_ID` is
          set), which owns the bottom-right ~108px of the viewport at every
          breakpoint. Nothing reserves that corner globally — the only rule
          that ever did is commented out in `theme/index.css`. QA ENG-5448.  */}
      <div className="flex justify-start gap-12 border-t border-surface-border p-24">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {/* `Send Request`, capitalised as the prototype's `submitLabel()` writes
            it. Shipped lower-cased the R, which put the drawer's primary action
            one character off the design it is diffed against (QA ENG-5734). The
            `customer_introduced` variant of this label, `Send Invitation`, is
            not reachable yet: that path is ENG-5699. */}
        <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
          Send Request
        </Button>
      </div>
    </Drawer>
  );
}

/**
 * Section shell for the drawer's five parts.
 *
 * Deliberately NOT `live/Section.tsx`, which is the Hub panels' bordered card
 * with an `hd-6` heading. Inside a drawer that chrome would box every field
 * group in its own card; the prototype's drawer uses a bare heading over the
 * fields, so this mirrors that instead (`subtitle-1`, matching the local
 * `Section` in `components/HireExpertDrawer/ProjectSection.tsx`).
 */
function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  // `h2`, where the prototype's own `Section` writes `h3`. These are the FIRST
  // headings in the drawer: `componentsv2/Drawer` renders none of its own and
  // the header shows breadcrumb text, so an `h3` here starts the document at
  // level 3 and skips two. The level is semantics; `subtitle-1` carries the
  // look, so this is identical on screen to the story it is diffed against.
  //
  // Named regions rather than bare `<section>`s: a sectioning element with no
  // accessible name is not announced as a region at all, so the visible
  // heading has to be wired to it.
  const headingId = useId();
  return (
    <section className="flex flex-col gap-12" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-on-surface subtitle-1">
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * Where this expert came from, under their name in the Expert section.
 *
 * The prototype writes one line per creation path. Only two of the four are
 * reachable today: `customer_introduced` is ENG-5699 and unbuilt, and
 * `from_candidate_card` has no live entry point, so its line would name an
 * expert request the drawer is not given. Both fall through to the generic
 * line rather than to an empty string, so a path that becomes reachable later
 * degrades to something true instead of a blank row.
 */
function expertSourceLine(path: EngagementCreationPath): string {
  switch (path) {
    case EngagementCreationPath.PostConsultation:
      return 'From your recent consultation';
    case EngagementCreationPath.FromProfile:
      return 'From expert profile';
    default:
      return 'Selected for this engagement';
  }
}

const PROJECT_HELP_ID = 'hire-expert-project-help';

/**
 * The project options read, and the only place it happens.
 *
 * It exists to make the account a REQUIRED argument. `hireExpertProjectOptions`
 * declares `$groupId: String!`, but a non-null variable only stops `null`, not
 * `''` — and `''` is the dangerous value, because the api reads a blank
 * `group_id` as "no group filter" and answers with every account the viewer
 * belongs to. Taking `account` rather than `account | undefined` means there is
 * no blank to build, so the leak is impossible rather than merely skipped.
 *
 * `open` is still a skip rather than a mount condition: `Drawer` renders its
 * children while closed, so without it the read would fire for a drawer nobody
 * has opened.
 */
function ProjectPickerSection({
  open,
  account,
  value,
  error,
  onChange,
}: {
  open: boolean;
  account: ActiveAccount;
  value: string;
  error: string | null;
  onChange: (projectId: string) => void;
}): JSX.Element {
  const options = useQuery(HIRE_EXPERT_PROJECT_OPTIONS, {
    variables: { first: PROJECT_OPTIONS_PAGE_SIZE, groupId: account.id },
    skip: !open,
  });

  return (
    <ProjectPicker
      options={options.data?.projects?.edges ?? []}
      accountName={account.name ?? null}
      loading={options.loading}
      failed={options.error !== undefined}
      onRetry={() => void options.refetch()}
      value={value}
      error={error}
      onChange={onChange}
    />
  );
}

/**
 * "You have no project here." Shared, because it is reached two ways: an
 * account whose read came back empty, and no account context at all. Identical
 * copy either way, so it is one component rather than two that must be kept in
 * step.
 */
function ProjectRequiredNotice({ accountName }: { accountName: string | null }): JSX.Element {
  return (
    <div className="flex flex-col gap-8 rounded-lg border border-dashed border-surface-border bg-surface-elevated px-20 py-16">
      <span className="text-on-surface subtitle-2">Project required</span>
      <span className="text-on-surface-muted body-2">
        {accountName
          ? `No project on ${accountName} lists you as a member. Open one there, or switch to another account, then hire from here.`
          : 'Engagements belong to a project. Open one you are a member of, then hire from here.'}
      </span>
    </div>
  );
}

interface ProjectPickerProps {
  options: readonly { node?: { id: string; name?: string | null } | null }[];
  /** The account the list was scoped to, named so the scope is visible. */
  accountName: string | null;
  loading: boolean;
  failed: boolean;
  value: string;
  error: string | null;
  onChange: (projectId: string) => void;
  onRetry: () => void;
}

/**
 * The `from_profile` project step.
 *
 * A native `<select>` on purpose, and the prototype flagged why:
 * `componentsv2` has no single-select form field. `Select` is a multi-select
 * autocomplete and `Dropdown` is a sort-style trigger with no label, error or
 * helper-text affordances. Styling a native select here beats quietly extending
 * the primitive set from inside a feature.
 *
 * The empty state is a real state, not a guard. A customer with no project they
 * belong to cannot open an engagement at all, and saying so is more use than an
 * empty dropdown. It does not offer to create a project: that lives on the
 * projects surface, and putting a second creation flow inside this drawer is how
 * one drawer becomes two features.
 *
 * Every state here NAMES THE ACCOUNT, and that is a fix rather than a
 * decoration (ENG-5729). The list is scoped to the account the viewer is acting
 * in, so a viewer who belongs to two accounts sees a subset of their projects,
 * and without the account named that is indistinguishable from the silent
 * omission this ticket is about. The account is the one thing the viewer can act
 * on: switching context is what makes the missing project appear.
 */
function ProjectPicker({
  options,
  accountName,
  loading,
  failed,
  value,
  error,
  onChange,
  onRetry,
}: ProjectPickerProps): JSX.Element {
  const projects = options
    .map((edge) => edge?.node)
    .filter((node): node is { id: string; name?: string | null } => Boolean(node?.id));

  if (loading && projects.length === 0) {
    return <p className="text-on-surface-muted body-2">Loading your projects…</p>;
  }

  // A failed read is not an answer about the customer's account. Falling through
  // to the empty state would tell them "you belong to no open project" on the
  // strength of a dropped connection, and terminally, with nothing to retry.
  if (failed && projects.length === 0) {
    return (
      <div className="flex flex-col gap-8 rounded-lg border border-surface-border bg-surface-elevated px-20 py-16">
        <span className="text-on-surface subtitle-2">We couldn&rsquo;t load your projects</span>
        <span className="text-on-surface-muted body-2">
          The connection may have dropped. Try again in a moment.
        </span>
        <div>
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return <ProjectRequiredNotice accountName={accountName} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <label htmlFor={PROJECT_SELECT_ID} className="text-on-surface-muted util-caption">
        Project
      </label>
      <select
        id={PROJECT_SELECT_ID}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // The red border is the only other error signal, so without these the
        // failure is invisible to a screen reader and to anyone who cannot
        // distinguish the border colour.
        aria-invalid={error !== null}
        aria-describedby={error ? PROJECT_ERROR_ID : PROJECT_HELP_ID}
        className={`min-h-48 rounded-md border bg-surface-card px-12 py-10 text-on-surface body-2 focus:border-brand-primary focus:outline-none ${
          error ? 'border-red-600' : 'border-surface-border'
        }`}
      >
        <option value="">Select a project</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name ?? 'Untitled project'}
          </option>
        ))}
      </select>
      {error ? (
        <span id={PROJECT_ERROR_ID} role="alert" className="text-red-600 util-caption">
          {error}
        </span>
      ) : (
        <span id={PROJECT_HELP_ID} className="text-on-surface-muted util-caption">
          {accountName
            ? `Projects on ${accountName} where you are a member, newest first.`
            : 'Projects where you are a member, newest first.'}
        </span>
      )}
    </div>
  );
}

type Preview = NonNullable<EngagementPricingPreviewQuery['engagementPricingPreview']>;

/**
 * Where pricing settles, and whether this size needs approval. No money.
 *
 * ENG-5731, superseding FB-174. The drawer prices off the expert's **profile**
 * rate — typically their higher consultation rate — so every figure this block
 * used to show was one the expert had not agreed to, and often above what they
 * would settle at. Brian reversed FB-174's ask on 2026-09-11 after seeing the
 * band schedule built: a loaded number shown before the offer can lose a deal we
 * are actively steering toward a lower rate. So no rate, no subtotal and no
 * not-to-exceed total here, at any hours value, until an offer exists.
 *
 * The section around it keeps its heading: only the body changed, which is how
 * the prototype's own unpriceable variant behaves.
 *
 * The query behind it still runs. `requiresGovernance` is the one answer the
 * customer needs before they submit — whether this size needs account approval
 * before work can start — and it falls out of the same server-side computation
 * that used to produce the prices. Only the display went; see
 * `ENGAGEMENT_PRICING_PREVIEW`, whose selection set is now that single field, so
 * there is no longer a money figure in scope to render by accident.
 *
 * `engagement.preview_rate_unavailable` stays a state rather than a failure: the
 * expert has no usable rate yet, and the request can still be sent because
 * pricing is settled at the offer step either way. Only a genuinely transient
 * error offers a retry.
 */
function OfferPricingNotice({
  previewable,
  blockedOn,
  preview,
  loading,
  failed,
  retryable,
  onRetry,
}: {
  previewable: boolean;
  blockedOn: PreviewBlock;
  preview: Preview | null;
  loading: boolean;
  failed: boolean;
  retryable: boolean;
  onRetry: () => void;
}): JSX.Element {
  const approval = approvalLine(previewable, blockedOn, preview, loading, failed);
  return (
    <div className="flex flex-col gap-8 rounded-md border border-surface-border bg-surface-secondary p-16">
      {/* Unconditional, in every state including before hours are entered:
          this is the drawer's answer to "what will this cost", and a sentence
          that appeared only once the server had replied would leave the
          question unanswered in exactly the states where nothing else is said. */}
      <p className="text-on-surface-muted body-2">
        Pricing is confirmed at the offer step. The expert sets their rate and the not-to-exceed
        total in their offer, and you see both before you accept.
      </p>

      {/* `role="status"`, where the static paragraph above deliberately has
          none: this line SWAPS as the query resolves, from "checking" to the
          governance verdict, and a screen-reader user not parked on this node
          would otherwise never hear the answer. Same discipline as
          `AccountRollupFilters.tsx:118`, which marks its loading and failed
          lines and leaves the neighbouring static notice alone; `role="status"`
          rather than `aria-live` because the codebase has twelve of the former
          and none of the latter, including `OverviewPanel.tsx:68` and the
          prototype section this component is modelled on. */}
      {approval === null ? null : (
        <div className="flex items-start gap-4">
          <p className="text-on-surface-muted body-2" role="status">
            {approval}
          </p>
          {/* Outside the status node, so the live region announces the verdict
              and not a button. The same definition the Settings tab shows
              (ENG-5757), with no amount: ENG-5731 keeps figures out of the
              drawer. */}
          {approval === OVER_THRESHOLD || approval === WITHIN_THRESHOLD ? (
            <InfoTip
              term={TERMS.governanceThreshold.label}
              definition={TERMS.governanceThreshold.definition}
            />
          ) : null}
        </div>
      )}

      {/* Gone while the retry is in flight, not merely disabled: the approval
          line above has already become "Checking whether this needs approval…",
          so a greyed-out button beneath it would be a second, redundant way of
          saying the same thing. It comes back only if the retry fails again. */}
      {retryable && !loading ? (
        <div>
          <Button variant="secondary" onClick={onRetry}>
            Check again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The two verdicts name the setting by its Settings-tab label, "governance
 * threshold", so the definition beside them is recognisably the same term
 * (ENG-5757). They used to say "account threshold".
 */
const OVER_THRESHOLD =
  'This size is over the governance threshold, so it needs governance approval before work can start.';
const WITHIN_THRESHOLD =
  'This size is within the governance threshold and needs no separate approval.';

/** Why the server has not answered yet, when it has not been asked. */
type PreviewBlock = 'project' | 'hours' | 'invalid-hours' | null;

/**
 * The approval half of the notice, one sentence per state.
 *
 * Neither threshold branch names a dollar amount — "over the governance threshold"
 * is the whole customer-facing fact, and the threshold itself is an account
 * setting they have no reason to read off a hire form.
 *
 * Returns null for `invalid-hours`, which is the one state that already has a
 * message: the Hours field carries the account minimum, or the number it
 * refuses, right where the customer typed it. A second, vaguer copy of the same
 * complaint down here would only compete with it.
 */
function approvalLine(
  previewable: boolean,
  blockedOn: PreviewBlock,
  preview: Preview | null,
  loading: boolean,
  failed: boolean
): string | null {
  // Nothing to price against on this path, and no hours the customer could
  // enter would change that — so this is a statement, not a prompt.
  if (!previewable) return 'Whether this needs approval is settled once an expert is attached.';
  // Each of these skips the query, so `loading` and `failed` below are both
  // false here and the order between them does not matter.
  if (blockedOn === 'project') return 'Select a project to see whether this needs approval.';
  // Not "estimated hours" (ENG-5735): the field's own help text calls this
  // number the not-to-exceed ceiling, and a prompt 180px below it calling the
  // same number an estimate contradicts it. The field label keeps the word
  // because it is the prototype's, which is why only the prompt changes here.
  if (blockedOn === 'hours') return 'Enter hours to see whether this needs approval.';
  if (blockedOn === 'invalid-hours') return null;
  if (loading) return 'Checking whether this needs approval…';
  if (failed) return 'Approval requirements are confirmed at the offer step.';
  // Not reachable through the blocks above, which is exactly why it is not a
  // prompt: nothing the customer could type would change a null the server sent.
  if (preview === null) return null;
  return preview.requiresGovernance ? OVER_THRESHOLD : WITHIN_THRESHOLD;
}
